import { useState } from "react";
import { Loader2, KeyRound, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const PwField = ({ id, label, value, onChange, testid }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} data-testid={testid} type="password" value={value} onChange={(e) => onChange(e.target.value)} placeholder="••••••••" />
  </div>
);

export const ChangePasswordDialog = ({ open, setOpen }) => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setCur(""); setNext(""); setConfirm(""); };

  const submit = async () => {
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("New password and confirmation do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated successfully");
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(apiError(e, "Could not change password"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2"><KeyRound className="h-5 w-5" /> Change Password</DialogTitle>
          <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <PwField id="cur-pw" testid="current-password-input" label="Current Password" value={cur} onChange={setCur} />
          <PwField id="new-pw" testid="new-password-input" label="New Password" value={next} onChange={setNext} />
          <PwField id="confirm-pw" testid="confirm-password-input" label="Confirm New Password" value={confirm} onChange={setConfirm} />
        </div>
        <DialogFooter>
          <Button data-testid="change-password-submit" onClick={submit} disabled={saving} className="gap-2 font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ForcePasswordChange = () => {
  const { user, setUser } = useAuth();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user || !user.must_change_password) return null;

  const submit = async () => {
    if (next.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/set-password", { new_password: next });
      toast.success("Password set — welcome to TSG AI Hub");
      setUser({ ...user, must_change_password: false });
    } catch (e) {
      toast.error(apiError(e, "Could not set password"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="force-password-change" className="fixed inset-0 z-50 bg-primary/95 backdrop-blur flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl">
        <div className="h-11 w-11 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
          <ShieldAlert className="h-6 w-6 text-amber-600" />
        </div>
        <h2 className="font-display text-xl font-bold tracking-tight">Set a new password</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-5">For your security, please create a new password before continuing.</p>
        <div className="space-y-4">
          <PwField id="fp-new" testid="force-new-password-input" label="New Password" value={next} onChange={setNext} />
          <PwField id="fp-confirm" testid="force-confirm-password-input" label="Confirm New Password" value={confirm} onChange={setConfirm} />
        </div>
        <Button data-testid="force-password-submit" onClick={submit} disabled={saving} className="w-full mt-5 gap-2 font-semibold h-11">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & Continue"}
        </Button>
      </div>
    </div>
  );
};
